import {
	createFileRoute,
	Link,
	type LinkProps,
	Outlet,
	useMatchRoute,
	useNavigate,
	useParams,
	useRouteContext,
	useRouter,
} from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useCopyToClipboard } from "@uidotdev/usehooks";
import type { icons } from "lucide-react";
import { ChevronRight } from "lucide-react";
import React, { Suspense, useState } from "react";
import { commitLocalUpdate, useRelayEnvironment } from "react-relay";
import { toast } from "sonner";
import { ClubSelector } from "@/components/club-selector";
import { Icon } from "@/components/icon";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarHeader,
	SidebarInset,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
	SidebarProvider,
	SidebarTrigger,
} from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { CreateAdminDialog } from "@/dialogs/admin-create-dialog";
import { CreateApiKeyDialog } from "@/dialogs/api-key-create-dialog";
import { CreateBadgeDialog } from "@/dialogs/badge-create-dialog";
import { CourseCategoryCreateDialog } from "@/dialogs/course-category-create-dialog";
import { CreateGroupDialog } from "@/dialogs/group-create-dialog";
import { CreateTagDialog } from "@/dialogs/tag-create-dialog";
import { logout as logoutHandler } from "@/handlers/logout";

type NavItem = {
	title: string;
	url: string;
	icon: keyof typeof icons;
	isActive?: boolean;
	children?: { title: string; url: string; isActive: boolean }[];
};

function PageSkeleton() {
	return (
		<div className="p-6 flex flex-col gap-6">
			<div className="overflow-hidden rounded-md border">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>
								<Skeleton className="h-4 w-24 bg-muted-foreground" />
							</TableHead>
							<TableHead>
								<Skeleton className="h-4 w-32 bg-muted-foreground" />
							</TableHead>
							<TableHead>
								<Skeleton className="h-4 w-28 bg-muted-foreground" />
							</TableHead>
							<TableHead>
								<Skeleton className="h-4 w-20 bg-muted-foreground" />
							</TableHead>
							<TableHead className="w-10" />
						</TableRow>
					</TableHeader>
					<TableBody>
						{Array.from({ length: 8 }).map((_, i) => (
							<TableRow key={i}>
								<TableCell>
									<Skeleton className="h-4 w-36 bg-muted-foreground" />
								</TableCell>
								<TableCell>
									<Skeleton className="h-4 w-28 bg-muted-foreground" />
								</TableCell>
								<TableCell>
									<Skeleton className="h-4 w-24 bg-muted-foreground" />
								</TableCell>
								<TableCell>
									<Skeleton className="h-4 w-20 bg-muted-foreground" />
								</TableCell>
								<TableCell>
									<Skeleton className="h-8 w-8 bg-muted-foreground" />
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
			<div className="flex items-center justify-between">
				<Skeleton className="h-4 w-48" />
				<div className="flex gap-2">
					<Skeleton className="h-9 w-24" />
					<Skeleton className="h-9 w-24" />
				</div>
			</div>
		</div>
	);
}

function ClubSelectorSkeleton() {
	return (
		<div className="flex items-center gap-3 rounded-md p-2">
			<Skeleton className="size-10 rounded-full" />
			<Skeleton className="h-4 w-28" />
		</div>
	);
}

export const Route = createFileRoute("/_logged/c/$club/(sidebar)")({
	component: RouteComponent,
});

function TopHeaderActions() {
	const matchRoute = useMatchRoute();
	const { club } = useParams({ from: Route.id });
	const [isBadgeDialogOpen, setIsBadgeDialogOpen] = useState(false);
	const [isTagDialogOpen, setIsTagDialogOpen] = useState(false);
	const [isAdminDialogOpen, setIsAdminDialogOpen] = useState(false);
	const [isGroupDialogOpen, setIsGroupDialogOpen] = useState(false);
	const [isCourseCategoryDialogOpen, setIsCourseCategoryDialogOpen] =
		useState(false);
	const [isApiKeyDialogOpen, setIsApiKeyDialogOpen] = useState(false);

	const actions = [
		{
			icon: "Plus",
			title: "Criar Seção",
			url: "/c/$club/area-de-membros/cursos",
			onClick: () => setIsCourseCategoryDialogOpen(true),
		},
		{
			icon: "Plus",
			title: "Criar Grupo",
			url: "/c/$club/area-de-membros/comunidade",
			onClick: () => setIsGroupDialogOpen(true),
		},
		{
			icon: "Plus",
			title: "Criar Evento",
			url: "/c/$club/area-de-membros/eventos",
			linkTo: "/c/$club/area-de-membros/eventos/novo",
		},
		{
			icon: "Plus",
			title: "Criar Post",
			url: "/c/$club/moderacao",
			linkTo: "/c/$club/moderacao/posts/novo",
		},
		// TODO: temporariamente removido
		// {
		// 	icon: "Plus",
		// 	title: "Nova Fonte",
		// 	url: "/c/$club/ia/fontes-de-dados",
		// },

		{
			icon: "Plus",
			title: "Criar Oferta",
			url: "/c/$club/monetizacao/ofertas",
			linkTo: "/c/$club/configuracoes/integracoes/checkout/produto/novo",
		},

		{
			icon: "Plus",
			title: "Importar Emails",
			url: "/c/$club/audiencia/membros",
			linkTo: "/c/$club/audiencia/membros/novo",
		},
		{
			title: "Criar Emblema",
			icon: "Plus",
			url: "/c/$club/audiencia/emblemas",
			onClick: () => setIsBadgeDialogOpen(true),
		},
		{
			icon: "Plus",
			title: "Criar Tag",
			url: "/c/$club/audiencia/tags",
			onClick: () => setIsTagDialogOpen(true),
		},

		{
			icon: "Plus",
			title: "Criar Workflow",
			url: "/c/$club/automacoes/workflows",
			linkTo: "/c/$club/workflows/novo",
		},
		{
			icon: "Plus",
			title: "Criar Template",
			url: "/c/$club/automacoes/templates-de-email",
			linkTo: "/c/$club/templates/novo",
		},

		{
			title: "Convidar Administradores",
			url: "/c/$club/configuracoes/administradores",
			icon: "Plus",
			onClick: () => setIsAdminDialogOpen(true),
		},
		{
			icon: "Plus",
			title: "Criar API Key",
			url: "/c/$club/configuracoes/api-keys",
			onClick: () => setIsApiKeyDialogOpen(true),
		},
	];

	const action = actions.find((ac) => Boolean(matchRoute({ to: ac.url })));

	if (!action) {
		return null;
	}

	return (
		<>
			<div>
				{action.linkTo ? (
					<Button size="sm" asChild>
						<Link to={action.linkTo} params={{ club }}>
							<Icon name={"Plus"} />
							{action.title}
						</Link>
					</Button>
				) : (
					<Button size="sm" onClick={action.onClick}>
						<Icon name={"Plus"} />
						{action.title}
					</Button>
				)}
			</div>
			{matchRoute({ to: "/c/$club/audiencia/tags" }) && (
				<CreateTagDialog
					club={club}
					open={isTagDialogOpen}
					onClose={() => setIsTagDialogOpen(false)}
				/>
			)}
			{matchRoute({ to: "/c/$club/audiencia/emblemas" }) && (
				<CreateBadgeDialog
					club={club}
					open={isBadgeDialogOpen}
					onClose={() => setIsBadgeDialogOpen(false)}
				/>
			)}
			{matchRoute({ to: "/c/$club/configuracoes/administradores" }) && (
				<CreateAdminDialog
					club={club}
					open={isAdminDialogOpen}
					onClose={() => setIsAdminDialogOpen(false)}
				/>
			)}
			{matchRoute({ to: "/c/$club/area-de-membros/comunidade" }) && (
				<CreateGroupDialog
					club={club}
					open={isGroupDialogOpen}
					onClose={() => setIsGroupDialogOpen(false)}
				/>
			)}
			{matchRoute({ to: "/c/$club/area-de-membros/cursos" }) && (
				<CourseCategoryCreateDialog
					club={club}
					open={isCourseCategoryDialogOpen}
					onClose={() => setIsCourseCategoryDialogOpen(false)}
				/>
			)}
			{matchRoute({ to: "/c/$club/configuracoes/api-keys" }) && (
				<CreateApiKeyDialog
					club={club}
					open={isApiKeyDialogOpen}
					onClose={() => setIsApiKeyDialogOpen(false)}
				/>
			)}
		</>
	);
}

function RouteComponent() {
	const { club } = useParams({ from: Route.id });
	const matchRoute = useMatchRoute();
	const {
		session,
		club: clubData,
		clubs,
	} = useRouteContext({ from: Route.id });
	const environment = useRelayEnvironment();
	const navigate = useNavigate();
	const router = useRouter();
	const logout = useServerFn(logoutHandler);
	const [, copyToClipboard] = useCopyToClipboard();

	const activeDomain = clubData.domains.find((d) => d.enabled)?.domain;
	const memberAreaUrl = activeDomain ? `https://${activeDomain}` : undefined;

	const isClubAdmin = clubData.permissions.some((p) => p.slug === "CLUB_ADMIN");

	const navItems: NavItem[] = [
		...(isClubAdmin
			? [
					{
						title: "Relatórios",
						url: "/c/$club/analytics",
						icon: "ChartLine" as const,
						isActive: Boolean(matchRoute({ to: "/c/$club/analytics" })),
					},
					{
						title: "Área de Membros",
						url: "/c/$club/area-de-membros",
						icon: "AppWindow" as const,
						children: [
							{
								title: "Cursos",
								url: "/c/$club/area-de-membros/cursos",
								isActive: Boolean(
									matchRoute({ to: "/c/$club/area-de-membros/cursos" }),
								),
							},
							{
								title: "Comunidade",
								url: "/c/$club/area-de-membros/comunidade",
								isActive: Boolean(
									matchRoute({ to: "/c/$club/area-de-membros/comunidade" }),
								),
							},
							{
								title: "Eventos",
								url: "/c/$club/area-de-membros/eventos",
								isActive: Boolean(
									matchRoute({ to: "/c/$club/area-de-membros/eventos" }),
								),
							},
							{
								title: "Ranking",
								url: "/c/$club/area-de-membros/ranking",
								isActive: Boolean(
									matchRoute({ to: "/c/$club/area-de-membros/ranking" }),
								),
							},
							{
								title: "Personalização",
								url: "/c/$club/area-de-membros/personalizacao",
								isActive: Boolean(
									matchRoute({ to: "/c/$club/area-de-membros/personalizacao" }),
								),
							},
						],
					},
				]
			: []),

		{
			title: "Moderação",
			url: "/c/$club/moderacao",
			icon: "Shield" as const,
			isActive: Boolean(matchRoute({ to: "/c/$club/moderacao" })),
		},

		...(isClubAdmin
			? [
					{
						title: "IA",
						url: "/c/$club/ia",
						icon: "Sparkles" as const,
						children: [
							{
								title: "Personalização",
								url: "/c/$club/ia/personalizacao",
								isActive: Boolean(
									matchRoute({ to: "/c/$club/ia/personalizacao" }),
								),
							},
							{
								title: "Acessos",
								url: "/c/$club/ia/acessos",
								isActive: Boolean(matchRoute({ to: "/c/$club/ia/acessos" })),
							},
							{
								title: "Fontes de Dados",
								url: "/c/$club/ia/fontes-de-dados",
								isActive: Boolean(
									matchRoute({ to: "/c/$club/ia/fontes-de-dados" }),
								),
							},
						],
					},
					{
						title: "Monetização",
						url: "/c/$club/monetizacao",
						icon: "DollarSign" as const,
						children: [
							{
								title: "Ofertas",
								url: "/c/$club/monetizacao/ofertas",
								isActive: Boolean(
									matchRoute({ to: "/c/$club/monetizacao/ofertas" }),
								),
							},
						],
					},
					{
						title: "Audiência",
						url: "/c/$club/audiencia",
						icon: "UsersRound" as const,
						children: [
							{
								title: "Membros",
								url: "/c/$club/audiencia/membros",
								isActive: Boolean(
									matchRoute({ to: "/c/$club/audiencia/membros" }),
								),
							},
							{
								title: "Emblemas",
								url: "/c/$club/audiencia/emblemas",
								isActive: Boolean(
									matchRoute({ to: "/c/$club/audiencia/emblemas" }),
								),
							},
							{
								title: "Tags",
								url: "/c/$club/audiencia/tags",
								isActive: Boolean(
									matchRoute({ to: "/c/$club/audiencia/tags" }),
								),
							},
						],
					},
					{
						title: "Automações",
						url: "/c/$club/automacoes",
						icon: "Zap" as const,
						children: [
							{
								title: "Workflows",
								url: "/c/$club/automacoes/workflows",
								isActive: Boolean(
									matchRoute({ to: "/c/$club/automacoes/workflows" }),
								),
							},
							{
								title: "Templates de Email",
								url: "/c/$club/automacoes/templates-de-email",
								isActive: Boolean(
									matchRoute({ to: "/c/$club/automacoes/templates-de-email" }),
								),
							},
						],
					},
					{
						title: "Configurações",
						url: "/c/$club/configuracoes",
						icon: "Settings" as const,
						children: [
							{
								title: "Administradores",
								url: "/c/$club/configuracoes/administradores",
								isActive: Boolean(
									matchRoute({ to: "/c/$club/configuracoes/administradores" }),
								),
							},
							{
								title: "Integrações",
								url: "/c/$club/configuracoes/integracoes",
								isActive: Boolean(
									matchRoute({ to: "/c/$club/configuracoes/integracoes" }),
								),
							},
							{
								title: "Domínios",
								url: "/c/$club/configuracoes/dominios",
								isActive: Boolean(
									matchRoute({ to: "/c/$club/configuracoes/dominios" }),
								),
							},
							{
								title: "Meu Plano",
								url: "/c/$club/configuracoes/meu-plano",
								isActive: Boolean(
									matchRoute({ to: "/c/$club/configuracoes/meu-plano" }),
								),
							},
							{
								title: "Afiliados",
								url: "/c/$club/configuracoes/afiliados",
								isActive: Boolean(
									matchRoute({ to: "/c/$club/configuracoes/afiliados" }),
								),
							},
							{
								title: "API Keys",
								url: "/c/$club/configuracoes/api-keys",
								isActive: Boolean(
									matchRoute({ to: "/c/$club/configuracoes/api-keys" }),
								),
							},
						],
					},
				]
			: []),
	];

	const breadcrumbs: [LinkProps["to"], string[]][] = [
		["/c/$club/analytics", ["Analytics"]],
		["/c/$club/area-de-membros/cursos", ["Área de Membros", "Cursos"]],
		[
			"/c/$club/area-de-membros/cursos/$category/importar",
			["Área de Membros", "Cursos", "Importar"],
		],
		[
			"/c/$club/area-de-membros/cursos/$slug/timeline",
			["Área de Membros", "Cursos", "Cronograma de Liberação"],
		],
		["/c/$club/area-de-membros/comunidade", ["Área de Membros", "Comunidade"]],
		["/c/$club/area-de-membros/eventos", ["Área de Membros", "Eventos"]],
		[
			"/c/$club/area-de-membros/eventos/novo",
			["Área de Membros", "Eventos", "Criar Evento"],
		],
		[
			"/c/$club/area-de-membros/eventos/$slug/editar",
			["Área de Membros", "Eventos", "Editar Evento"],
		],
		["/c/$club/area-de-membros/ranking", ["Área de Membros", "Ranking"]],
		[
			"/c/$club/area-de-membros/personalizacao",
			["Área de Membros", "Personalização"],
		],
		["/c/$club/moderacao", ["Moderação"]],
		["/c/$club/moderacao/posts/novo", ["Moderação", "Criar Post"]],
		["/c/$club/moderacao/posts/$slug/editar", ["Moderação", "Editar Post"]],
		["/c/$club/moderacao/regras", ["Moderação", "Regras"]],
		["/c/$club/ia/personalizacao", ["IA", "Personalização"]],
		["/c/$club/ia/acessos", ["IA", "Acessos"]],
		["/c/$club/ia/fontes-de-dados", ["IA", "Fontes de Dados"]],
		["/c/$club/monetizacao/ofertas", ["Monetização", "Ofertas"]],
		["/c/$club/audiencia/membros", ["Audiência", "Membros"]],
		[
			"/c/$club/audiencia/membros/novo",
			["Audiência", "Membros", "Importar Emails"],
		],
		[
			"/c/$club/audiencia/membros/$username",
			["Audiência", "Membros", "Detalhes do Membro"],
		],
		["/c/$club/audiencia/emblemas", ["Audiência", "Emblemas"]],
		["/c/$club/audiencia/tags", ["Audiência", "Tags"]],
		["/c/$club/automacoes/workflows", ["Automações", "Workflows"]],
		[
			"/c/$club/automacoes/templates-de-email",
			["Automações", "Templates de Email"],
		],
		[
			"/c/$club/configuracoes/administradores",
			["Configurações", "Administradores"],
		],
		["/c/$club/configuracoes/integracoes", ["Configurações", "Integrações"]],
		[
			"/c/$club/configuracoes/integracoes/checkout/$slug/novo",
			["Configurações", "Integrações", "Nova Integração"],
		],
		[
			"/c/$club/configuracoes/integracoes/checkout/$id",
			["Configurações", "Integrações", "Detalhes da Integração"],
		],
		[
			"/c/$club/configuracoes/integracoes/checkout/produto/novo",
			["Configurações", "Integrações", "Nova Oferta"],
		],
		[
			"/c/$club/configuracoes/integracoes/checkout/produto/$id",
			["Configurações", "Integrações", "Editar Oferta"],
		],
		["/c/$club/configuracoes/dominios", ["Configurações", "Dominios"]],
		["/c/$club/configuracoes/meu-plano", ["Configurações", "Meu Plano"]],
		["/c/$club/configuracoes/afiliados", ["Configurações", "Afiliados"]],
		["/c/$club/configuracoes/api-keys", ["Configurações", "API Keys"]],
	] as const;

	const breadcrumbData =
		breadcrumbs.find((b) => matchRoute({ to: b[0] }))?.[1] || [];

	return (
		<SidebarProvider>
			<Sidebar>
				<SidebarHeader>
					<Suspense fallback={<ClubSelectorSkeleton />}>
						<ClubSelector clubs={clubs} />
					</Suspense>
				</SidebarHeader>

				<SidebarContent>
					<SidebarGroup>
						<SidebarMenu>
							{navItems.map((item) =>
								item.children ? (
									<SidebarMenuItem key={item.title} data-active>
										<Collapsible
											defaultOpen={item.children.some((c) =>
												Boolean(matchRoute({ to: c.url })),
											)}
											className="group/collapsible"
										>
											<CollapsibleTrigger asChild>
												<SidebarMenuButton tooltip={item.title}>
													<Icon name={item.icon} />
													<span>{item.title}</span>
													<ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
												</SidebarMenuButton>
											</CollapsibleTrigger>
											<CollapsibleContent>
												<SidebarMenuSub>
													{item.children.map((child) => (
														<SidebarMenuSubItem key={child.title}>
															<SidebarMenuSubButton
																asChild
																isActive={child.isActive}
															>
																<Link to={child.url} params={{ club }}>
																	{child.title}
																</Link>
															</SidebarMenuSubButton>
														</SidebarMenuSubItem>
													))}
												</SidebarMenuSub>
											</CollapsibleContent>
										</Collapsible>
									</SidebarMenuItem>
								) : (
									<SidebarMenuItem key={item.title}>
										<SidebarMenuButton
											asChild
											tooltip={item.title}
											isActive={item.isActive}
										>
											<Link to={item.url} params={{ club }}>
												<Icon name={item.icon} />
												<span>{item.title}</span>
											</Link>
										</SidebarMenuButton>
									</SidebarMenuItem>
								),
							)}
						</SidebarMenu>
					</SidebarGroup>
				</SidebarContent>

				<SidebarFooter>
					{memberAreaUrl && (
						<>
							<div className="flex gap-2">
								<Button
									variant="outline"
									className="text-foreground flex-1"
									asChild
								>
									<a
										href={memberAreaUrl}
										target="_blank"
										rel="noopener noreferrer"
									>
										<Icon name="Monitor" />
										Ver como Aluno
									</a>
								</Button>
								<Tooltip>
									<TooltipTrigger asChild>
										<Button
											variant="outline"
											size="icon"
											onClick={() => {
												copyToClipboard(memberAreaUrl);
												toast.success("Link copiado com sucesso!");
											}}
										>
											<Icon name="Copy" />
										</Button>
									</TooltipTrigger>
									<TooltipContent side="top">
										Copiar Link da Área de Membros
									</TooltipContent>
								</Tooltip>
							</div>

							<Separator className="mt-2" />
						</>
					)}

					<SidebarMenu>
						<SidebarMenuItem>
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<SidebarMenuButton
										size="lg"
										className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
									>
										<Avatar className="size-8">
											<AvatarImage
												src={session?.user?.photo?.signedUrl ?? undefined}
											/>
											<AvatarFallback>
												{session?.user?.avatarFallback ??
													session?.user?.displayName
														?.slice(0, 2)
														.toUpperCase() ??
													session?.user?.email?.slice(0, 2).toUpperCase()}
											</AvatarFallback>
										</Avatar>
										<div className="flex flex-col gap-0.5 leading-none text-left">
											<span className="font-medium text-sm truncate text-foreground">
												{session?.user?.displayName ?? session?.user?.email}
											</span>
											{session?.user?.displayName && (
												<span className="text-xs text-muted-foreground truncate">
													{session.user.email}
												</span>
											)}
										</div>
									</SidebarMenuButton>
								</DropdownMenuTrigger>
								<DropdownMenuContent
									className="w-(--radix-dropdown-menu-trigger-width)"
									align="start"
									side="top"
								>
									<DropdownMenuItem
										onClick={async () => {
											if (confirm("Tem certeza que deseja fazer logout?")) {
												logout().then(async () => {
													commitLocalUpdate(environment, (store) =>
														store.invalidateStore(),
													);

													router.invalidate();

													navigate({
														reloadDocument: true,
													});
												});
											}
										}}
									>
										<Icon name="LogOut" className="size-4" />
										Logout
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						</SidebarMenuItem>
					</SidebarMenu>
				</SidebarFooter>
			</Sidebar>

			<SidebarInset>
				<header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
					<div className="flex-1 flex items-center shrink-0 gap-2">
						<SidebarTrigger />

						<Separator
							orientation="vertical"
							className="mr-2 data-[orientation=vertical]:h-4"
						/>

						<Breadcrumb>
							<BreadcrumbList>
								{breadcrumbData.map((b, index, arr) => (
									<React.Fragment key={index}>
										{index === arr.length - 1 ? (
											<BreadcrumbPage className="hidden md:block">
												{b}
											</BreadcrumbPage>
										) : (
											<BreadcrumbItem className="hidden md:block">
												{b}
											</BreadcrumbItem>
										)}

										{index !== arr.length - 1 ? (
											<BreadcrumbSeparator className="hidden md:block" />
										) : null}
									</React.Fragment>
								))}
							</BreadcrumbList>
						</Breadcrumb>
					</div>

					<TopHeaderActions />
				</header>
				<Suspense fallback={<PageSkeleton />}>
					<div className="max-w-[1200px] mx-auto w-full">
						<Outlet />
					</div>
				</Suspense>
			</SidebarInset>
		</SidebarProvider>
	);
}
